import { canAnimateElement, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { bindNoteContentByteLimit } from './content-limit.js';

export function initGameDetailsNoteForm(documentRef = document) {
    const notesSection = documentRef.querySelector('[data-game-notes-section]');
    if (!(notesSection instanceof HTMLElement)) {
        return null;
    }

    const composer = notesSection.querySelector('[data-game-note-composer]');
    const addButton = notesSection.querySelector('[data-game-note-toggle]');
    const noteForm = notesSection.querySelector('[data-game-note-form]');
    const cancelButton = notesSection.querySelector('[data-game-note-cancel]');
    const noteTextarea = notesSection.querySelector('#game-note-content');
    const fileInput = notesSection.querySelector('#game-note-attachments');
    const fileSelection = notesSection.querySelector('[data-game-note-file-selection]');

    if (
        !(composer instanceof HTMLElement)
        || !(addButton instanceof HTMLButtonElement)
        || !(noteForm instanceof HTMLFormElement)
        || !(cancelButton instanceof HTMLButtonElement)
        || !(noteTextarea instanceof HTMLTextAreaElement)
        || !(fileInput instanceof HTMLInputElement)
        || !(fileSelection instanceof HTMLElement)
    ) {
        return null;
    }

    const syncSelectedFilesLabel = () => {
        if (fileInput.files === null || fileInput.files.length === 0) {
            fileSelection.hidden = true;
            fileSelection.textContent = '';

            return;
        }

        const normalizedNames = Array.from(fileInput.files).map((file, index) => {
            const normalizedName = file.name.trim();

            return normalizedName !== '' ? normalizedName : `File ${index + 1}`;
        });
        const maxVisibleNames = 3;
        const previewNames = normalizedNames.slice(0, maxVisibleNames);
        const remainingCount = normalizedNames.length - previewNames.length;

        const summary = `${normalizedNames.length} ${normalizedNames.length === 1 ? 'file' : 'files'} selected`;
        const details = previewNames.join(', ');
        fileSelection.textContent = remainingCount > 0
            ? `${summary}: ${details}, +${remainingCount} more`
            : `${summary}: ${details}`;
        fileSelection.hidden = false;
    };

    let isTransitioning = false;

    const applyExpandedState = (expanded) => {
        noteForm.hidden = !expanded;
        addButton.hidden = expanded;
        addButton.setAttribute('aria-expanded', String(expanded));
    };

    const resetMotionStyles = () => {
        composer.classList.remove('game-note-composer-morphing');
        composer.style.removeProperty('height');
    };

    const focusExpandedState = (expanded) => {
        if (expanded) {
            noteTextarea.focus();
            noteTextarea.setSelectionRange(noteTextarea.value.length, noteTextarea.value.length);
            return;
        }

        addButton.focus();
    };

    const transitionComposer = async (expanded) => {
        if (isTransitioning) {
            return;
        }

        if (!canAnimateElement(composer, documentRef.defaultView)) {
            applyExpandedState(expanded);
            focusExpandedState(expanded);
            return;
        }

        isTransitioning = true;
        const startHeight = composer.getBoundingClientRect().height;
        noteForm.hidden = false;
        addButton.hidden = false;
        addButton.setAttribute('aria-expanded', String(expanded));
        composer.classList.add('game-note-composer-morphing');
        const endHeight = (expanded ? noteForm : addButton).getBoundingClientRect().height;
        composer.style.setProperty('height', `${startHeight}px`);

        const duration = readCssTimeMs(
            composer,
            expanded ? '--game-note-composer-open-duration' : '--game-note-composer-close-duration',
            expanded ? 320 : 220,
        );
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
        const animations = [
            playAnimation(composer, [
                { height: `${startHeight}px` },
                { height: `${endHeight}px` },
            ], { duration, easing, fill: 'forwards' }),
            playAnimation(addButton, [
                { opacity: expanded ? 1 : 0, transform: `translateY(${expanded ? 0 : -4}px)` },
                { opacity: expanded ? 0 : 1, transform: `translateY(${expanded ? -4 : 0}px)` },
            ], { duration: Math.min(duration, 220), easing, fill: 'both' }),
            playAnimation(noteForm, [
                { opacity: expanded ? 0 : 1, transform: `translateY(${expanded ? 8 : 0}px)` },
                { opacity: expanded ? 1 : 0, transform: `translateY(${expanded ? 0 : 8}px)` },
            ], { duration, easing, fill: 'both' }),
        ].filter(Boolean);

        await settleAnimations(animations);
        applyExpandedState(expanded);
        cancelAnimations(animations);
        resetMotionStyles();
        focusExpandedState(expanded);
        isTransitioning = false;
    };

    bindNoteContentByteLimit(noteTextarea);
    const startsExpanded = noteTextarea.value !== '';
    applyExpandedState(startsExpanded);
    if (startsExpanded) {
        focusExpandedState(true);
    }
    noteForm.classList.remove('game-note-form-ssr-collapsed');

    addButton.addEventListener('click', async () => {
        await transitionComposer(true);
    });

    cancelButton.addEventListener('click', async () => {
        if (isTransitioning) {
            return;
        }

        await transitionComposer(false);
    });

    fileInput.addEventListener('change', syncSelectedFilesLabel);
}
